"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useGetSocialUser, useSocialPayMutations } from "@/hooks/social-pay";
import { BlockiesAvatar } from "@/components/blockies-avatar";
import {
  X,
  LogOut,
  Twitter,
  Link2,
  Unlink,
  Mail,
  MessageSquare,
  Copy,
  Check,
  Flame,
} from "lucide-react";

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, email, address, logout, linkEmail, unlinkEmail } = useAuth();
  const { data: socialUser } = useGetSocialUser();
  const { linkTwitter, unlinkTwitter, linkDiscord, unlinkDiscord } = useSocialPayMutations();

  const [copiedAddress, setCopiedAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCopyAddress = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    await logout();
    setIsLoading(false);
    onClose();
  };

  const formatAddress = (addr: string) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }} // ease-out-quad
            className="fixed top-1/2 left-1/2 w-full max-w-md bg-[#0E0D0B] border border-[#262523] rounded-lg z-50">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#262523]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#EA5555] rounded-lg flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-medium tracking-[-1.5px] text-white uppercase tracking-wider">
              USER SETTINGS
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#262523] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[#9CA3AF]" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Profile Section */}
          <div className="flex items-center gap-4">
            <BlockiesAvatar
              seed={address || ""}
              size={16}
              scale={4}
              className="border-0"
            />
            <div className="flex-1">
              <p className="text-white font-medium uppercase">
                {email || "ANONYMOUS USER"}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-[#9CA3AF] text-sm">
                  {address && formatAddress(address)}
                </p>
                <button
                  onClick={handleCopyAddress}
                  className="p-1 hover:bg-[#262523] rounded transition-colors"
                >
                  {copiedAddress ? (
                    <Check className="w-3 h-3 text-[#EA5555]" />
                  ) : (
                    <Copy className="w-3 h-3 text-[#9CA3AF]" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Email Section */}
          <div className="space-y-3">
            <h3 className="text-white text-sm font-medium tracking-[-1.5px] uppercase tracking-wider">
              EMAIL
            </h3>
            <div className="p-4 bg-[#1A1917] rounded-lg border border-[#262523]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#9CA3AF]" />
                  <div>
                    <p className="text-white text-sm">
                      {email || "NO EMAIL LINKED"}
                    </p>
                  </div>
                </div>
                {email ? (
                  <button
                    onClick={() => unlinkEmail(email)}
                    className="px-3 py-1.5 bg-[#262523] hover:bg-[#3A3A3A] text-white rounded text-xs uppercase font-medium transition-colors flex items-center gap-1"
                  >
                    <Unlink className="w-3 h-3" />
                    UNLINK
                  </button>
                ) : (
                  <button
                    onClick={() => linkEmail()}
                    className="px-3 py-1.5 bg-[#EA5555] hover:bg-[#FF6666] text-white rounded text-xs uppercase font-medium transition-colors flex items-center gap-1"
                  >
                    <Link2 className="w-3 h-3" />
                    LINK
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Social Accounts */}
          <div className="space-y-3">
            <h3 className="text-white text-sm font-medium tracking-[-1.5px] uppercase tracking-wider">
              SOCIAL ACCOUNTS
            </h3>

            <div className="space-y-2">
              {/* Twitter */}
              <div className="p-4 bg-[#1A1917] rounded-lg border border-[#262523]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Twitter className="w-5 h-5 text-[#9CA3AF]" />
                    <div>
                      <p className="text-white text-sm uppercase">X (TWITTER)</p>
                      {(socialUser?.twitterMetadata || socialUser?.twitter) && (
                        <p className="text-[#9CA3AF] text-xs">
                          @{socialUser?.twitterMetadata?.username || socialUser?.twitter?.username}
                        </p>
                      )}
                    </div>
                  </div>
                  {(socialUser?.twitterMetadata || socialUser?.twitter) ? (
                    <button
                      onClick={() => unlinkTwitter.mutate()}
                      disabled={unlinkTwitter.isPending}
                      className="px-3 py-1.5 bg-[#262523] hover:bg-[#3A3A3A] text-white rounded text-xs uppercase font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <Unlink className="w-3 h-3" />
                      UNLINK
                    </button>
                  ) : (
                    <button
                      onClick={() => linkTwitter.mutate({ redirectUrl: window.location.href })}
                      disabled={linkTwitter.isPending}
                      className="px-3 py-1.5 bg-[#EA5555] hover:bg-[#FF6666] text-white rounded text-xs uppercase font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <Link2 className="w-3 h-3" />
                      LINK
                    </button>
                  )}
                </div>
              </div>

              {/* Discord */}
              <div className="p-4 bg-[#1A1917] rounded-lg border border-[#262523]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-[#9CA3AF]" />
                    <div>
                      <p className="text-white text-sm uppercase">DISCORD</p>
                      {(socialUser?.discordMetadata || socialUser?.discord) && (
                        <p className="text-[#9CA3AF] text-xs">
                          @{socialUser?.discordMetadata?.username || socialUser?.discord?.username}
                        </p>
                      )}
                    </div>
                  </div>
                  {(socialUser?.discordMetadata || socialUser?.discord) ? (
                    <button
                      onClick={() => unlinkDiscord.mutate()}
                      disabled={unlinkDiscord.isPending}
                      className="px-3 py-1.5 bg-[#262523] hover:bg-[#3A3A3A] text-white rounded text-xs uppercase font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <Unlink className="w-3 h-3" />
                      UNLINK
                    </button>
                  ) : (
                    <button
                      onClick={() => linkDiscord.mutate()}
                      disabled={linkDiscord.isPending}
                      className="px-3 py-1.5 bg-[#EA5555] hover:bg-[#FF6666] text-white rounded text-xs uppercase font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <Link2 className="w-3 h-3" />
                      LINK
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            disabled={isLoading}
            className="w-full py-3 bg-[#262523] hover:bg-[#3A3A3A] text-white rounded-lg font-medium uppercase tracking-wider transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <LogOut className="w-5 h-5" />
            {isLoading ? "LOGGING OUT..." : "LOGOUT"}
          </button>
        </div>
      </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};